import * as React from 'react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui//button';
import { Eye, EyeOff } from 'lucide-react';

/**
 * InputPasswordCds component
 * @description
 * A password input field with a toggle button to show/hide the password.
 * @param {React.ComponentProps<'input'>} props
 * @param {React.RefObject<HTMLInputElement>} ref
 * @returns {React.ReactElement}
 * @example
 * ```tsx
 * <InputPasswordCds />
 * ```
 * @example
 * ```tsx
 *  <InputPasswordCds className="text-sm" placeholder="Enter your password" required />
 * ```
 *
 */
const InputPasswordCds = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, value, ...props }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const togglePasswordVisibility = () => setShowPassword(!showPassword);

    return (
      <div className="relative w-full">
        <Input
          type={showPassword ? 'text' : 'password'}
          ref={ref}
          className={className}
          value={value ?? ''}
          {...props}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
          onClick={togglePasswordVisibility}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
      </div>
    );
  }
);
InputPasswordCds.displayName = 'InputPasswordCds';

export { InputPasswordCds };
